import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';
import ContactForm from '../../components/ContactForm/ContactForm';
import { SERVICE_PILLARS } from '../../data/servicePillars';
import { IA_HUB, IA_SERVICES, IA_CASOS } from '../../config/iaBrand';
import {
  trackPhoneCall,
  trackEmailClick,
  trackDownload,
  trackContactMethod,
} from '../../components/Analytics/Analytics';
import { OFFICE_ADDRESS, OFFICE_MAPS_URL } from '../../config/office';
import './SponsorPage.css';

const PAGE_URL = 'https://e-asy.es/sponsor';
const CONTACT_EMAIL = 'comercial@e-asy.es';
const PHONE_DISPLAY = '+34 822 68 50 12';
const PHONE_TEL = '+34822685012';
const LINKEDIN_URL = 'https://www.linkedin.com/company/insideeasy/';
const MAPS_URL = OFFICE_MAPS_URL;

const FEATURED_PILLAR_ID = 'ingenieria-cuota';
const FEATURED_PILLAR = SERVICE_PILLARS.find((pillar) => pillar.id === FEATURED_PILLAR_ID);
const OTHER_PILLARS = SERVICE_PILLARS.filter((pillar) => pillar.id !== FEATURED_PILLAR_ID);

const MORE_LINKS = [
  { href: IA_HUB.path, label: IA_HUB.label },
  { href: IA_SERVICES.cumplimiento.path, label: IA_SERVICES.cumplimiento.shortLabel },
  { href: IA_SERVICES.agentes.path, label: IA_SERVICES.agentes.shortLabel },
  { href: IA_SERVICES.privada.path, label: IA_SERVICES.privada.shortLabel },
  { href: '/servicios/business-central', label: 'Business Central' },
  { href: '/servicios/azure', label: 'Azure' },
  { href: '/tercer-sector', label: 'Tercer sector' },
  { href: IA_CASOS.path, label: IA_CASOS.label },
];

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Tarjeta de contacto de E-ASY',
  url: PAGE_URL,
  mainEntity: {
    '@type': 'ProfessionalService',
    name: 'E-ASY',
    url: 'https://e-asy.es',
    telephone: '+34-822-68-50-12',
    email: CONTACT_EMAIL,
    address: {
      '@type': 'PostalAddress',
      streetAddress: OFFICE_ADDRESS,
      addressLocality: 'Santa Cruz de Tenerife',
      addressRegion: 'Canarias',
      addressCountry: 'ES',
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
  },
};

const buildVCard = () =>
  [
    'BEGIN:VCARD',
    'VERSION:3.0',
    'N:;E-ASY;;;',
    'FN:E-ASY',
    'ORG:E-ASY Consultoría Tecnológica',
    'TEL;TYPE=WORK,VOICE:+34822685012',
    'EMAIL;TYPE=INTERNET,WORK:comercial@e-asy.es',
    'ADR;TYPE=WORK:;;Carretera Santa María 22\\, Edificio Intech;Santa Cruz de Tenerife;;;España',
    'URL:https://e-asy.es',
    'NOTE:Consultoría tecnológica. Presencial en Tenerife y Madrid\\, y en remoto en toda España. Lunes a viernes de 9:00 a 17:00.',
    'END:VCARD',
  ].join('\r\n');

const SponsorPage = ({ darkMode, toggleDarkMode }) => {
  const handlePhoneClick = () => {
    trackPhoneCall(PHONE_DISPLAY);
    trackContactMethod('telefono-sponsor');
  };

  const handleEmailClick = () => {
    trackEmailClick(CONTACT_EMAIL);
    trackContactMethod('email-sponsor');
  };

  const handleSaveContact = () => {
    const blob = new Blob([buildVCard()], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'E-ASY.vcf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    trackDownload('E-ASY.vcf');
  };

  return (
    <div className={`sponsor-page${darkMode ? ' dark' : ''}`}>
      <SEO
        title="Tarjeta de contacto | E-ASY"
        description="Contacto de E-ASY: teléfono, email y oficina. Consultoría tecnológica en remoto y en toda España: ingeniería a cuota, IA en producción, Vulpin, web y Microsoft / Azure."
        keywords="contacto E-ASY, consultoría tecnológica Tenerife, teléfono E-ASY, Vulpin Suite"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-sponsor-contact" data={contactSchema} />
      <AnalyticsTracker
        pageTitle="Tarjeta de contacto - E-ASY"
        pageLocation="/sponsor"
        darkMode={darkMode}
      />

      <header className="sponsor-header">
        <Link to="/" className="sponsor-wordmark" aria-label="E-ASY, ir a la web">
          <span className="sponsor-wordmark-prefix">E-</span>
          <span>ASY</span>
        </Link>
        <div className="sponsor-header-actions">
          <Link to="/" className="sponsor-header-link">
            Ver la web
          </Link>
          <button
            type="button"
            className="sponsor-theme"
            onClick={toggleDarkMode}
            aria-label={darkMode ? 'Activar modo claro' : 'Activar modo oscuro'}
          >
            {darkMode ? 'Claro' : 'Oscuro'}
          </button>
        </div>
      </header>

      <main className="sponsor-main" id="contenido-principal">
        <section className="sponsor-hero">
          <p className="sponsor-kicker">Consultoría tecnológica</p>
          <h1>E-ASY</h1>
          <p className="sponsor-lead">
            Ordenamos la gestión, ponemos la IA en producción y dejamos un
            ingeniero en el equipo por una cuota mensual, con una consultora
            completa detrás. Presencial en Tenerife y Madrid, y en remoto en toda España.
          </p>
          <div className="sponsor-actions">
            <a className="sponsor-btn primary" href={`tel:${PHONE_TEL}`} onClick={handlePhoneClick}>
              Llamar
            </a>
            <a className="sponsor-btn" href="#escribir">
              Escribir
            </a>
            <button type="button" className="sponsor-btn" onClick={handleSaveContact}>
              Guardar contacto
            </button>
          </div>
        </section>

        <section className="sponsor-contact" aria-labelledby="sponsor-contact-title">
          <h2 id="sponsor-contact-title">Contacto</h2>
          <ul className="sponsor-facts">
            <li>
              <span className="sponsor-fact-label">Teléfono</span>
              <a href={`tel:${PHONE_TEL}`} onClick={handlePhoneClick}>
                {PHONE_DISPLAY}
              </a>
              <span className="sponsor-fact-note">Lunes a viernes, 9:00–17:00</span>
            </li>
            <li>
              <span className="sponsor-fact-label">Email</span>
              <a href={`mailto:${CONTACT_EMAIL}`} onClick={handleEmailClick}>
                {CONTACT_EMAIL}
              </a>
              <span className="sponsor-fact-note">Respuesta en menos de 24 horas</span>
            </li>
            <li>
              <span className="sponsor-fact-label">Oficina</span>
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer">
                {OFFICE_ADDRESS}
              </a>
              <span className="sponsor-fact-note">Tenerife, Canarias</span>
            </li>
            <li>
              <span className="sponsor-fact-label">LinkedIn</span>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
                E-ASY en LinkedIn
              </a>
              <span className="sponsor-fact-note">insideeasy</span>
            </li>
          </ul>
        </section>

        <section className="sponsor-featured" aria-labelledby="sponsor-featured-title">
          <p className="sponsor-kicker">Ingeniería a cuota</p>
          <h2 id="sponsor-featured-title">Un ingeniero en tu empresa, por una cuota mensual</h2>
          <p className="sponsor-section-lead">
            Desarrollo a medida dentro de tu gestión y acompañamiento en la
            digitalización. La cuota es del orden de lo que cuesta un especialista
            en plantilla; quien responde es toda la consultora.
          </p>
          <ul className="sponsor-benefits">
            <li>
              <strong>Sin contratar en plantilla.</strong> Ni selección, ni bajas,
              ni vacaciones que paren el trabajo.
            </li>
            <li>
              <strong>Software sobre cómo trabajáis.</strong> Lo que la herramienta
              que ya usáis no cubre.
            </li>
            <li>
              <strong>Prioridad del mes.</strong> El encargo cambia sin abrir un
              proyecto nuevo cada vez.
            </li>
            <li>
              <strong>Toda la consultora detrás.</strong> Vulpin, ciberseguridad,
              IA o Azure entran sin cambiar de proveedor.
            </li>
          </ul>
          <div className="sponsor-actions">
            <a className="sponsor-btn primary" href="#escribir">
              Hablar de la cuota
            </a>
            <Link className="sponsor-btn" to={FEATURED_PILLAR.href}>
              Ver el servicio
            </Link>
          </div>
        </section>

        <section className="sponsor-work" aria-labelledby="sponsor-work-title">
          <h2 id="sponsor-work-title">Qué más hacemos</h2>
          <p className="sponsor-section-lead">
            Cada línea tiene su página si quieres el detalle.
          </p>
          <ul className="sponsor-services">
            {OTHER_PILLARS.map((pillar) => (
              <li key={pillar.id}>
                <article className="sponsor-service">
                  <h3>
                    <Link to={pillar.href}>{pillar.title}</Link>
                  </h3>
                  <p>{pillar.short}</p>
                  <ul className="sponsor-chips">
                    {pillar.features.map((feature) => (
                      <li key={feature}>{feature}</li>
                    ))}
                  </ul>
                  <Link className="sponsor-more" to={pillar.href}>
                    Ver {pillar.title}
                  </Link>
                </article>
              </li>
            ))}
          </ul>

          <div className="sponsor-also">
            <h3>Si buscas un tema concreto</h3>
            <ul>
              {MORE_LINKS.map((item) => (
                <li key={item.href}>
                  <Link to={item.href}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="sponsor-form-section" id="escribir" aria-labelledby="sponsor-form-title">
          <h2 id="sponsor-form-title">Escríbenos</h2>
          <p className="sponsor-section-lead">
            Cuéntanos el caso. Te respondemos en menos de 24 horas.
          </p>
          <ContactForm
            darkMode={darkMode}
            source="tarjeta-sponsor"
            trackingEvent="sponsor_form"
            pageName="Tarjeta de contacto"
            subjectPrefix="[Tarjeta]"
            emptyMessageFallback="Mensaje desde la tarjeta de contacto (/sponsor)"
          />
          <p className="sponsor-privacy">
            Al enviar aceptas la{' '}
            <Link to="/politica-privacidad">política de privacidad</Link>.
          </p>
        </section>
      </main>

      <footer className="sponsor-footer">
        <p>E-ASY Consultoría Tecnológica y Agencia Digital, S.L.</p>
        <nav aria-label="Legal">
          <Link to="/aviso-legal">Aviso legal</Link>
          <Link to="/politica-privacidad">Privacidad</Link>
          <Link to="/politica-cookies">Cookies</Link>
        </nav>
      </footer>

      <nav className="sponsor-dock" aria-label="Acciones de contacto">
        <a href={`tel:${PHONE_TEL}`} onClick={handlePhoneClick}>
          Llamar
        </a>
        <a href={`mailto:${CONTACT_EMAIL}`} onClick={handleEmailClick}>
          Email
        </a>
        <a href="#escribir">Escribir</a>
      </nav>
    </div>
  );
};

export default SponsorPage;
